import { IconButton } from "@rosc/ui/v32/button";
import { Checkbox } from "@rosc/ui/v32/checkbox";
import { ChipLabel } from "@rosc/ui/v32/chip";
import { Input, Textarea } from "@rosc/ui/v32/input";
import {
  REVIEW_PRIMARY,
  REVIEW_QUIET,
  ReviewCardShell,
} from "@rosc/ui/v32/review-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@rosc/ui/v32/select";
import { Check, Lock, Pencil, PenLine, X } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CompletedForm,
  FormEntry,
  FormField,
} from "@/lib/form-completion/queries";
import {
  accept,
  type FormReviewState,
  setIdentifier,
  setValue,
  shownValue,
} from "@/lib/form-completion/review";
import { cn } from "@/lib/utils";

type StatusKey = FormEntry["status"] | "edited";

const statusOf = (
  entry: FormEntry,
  review: FormReviewState,
  pending: readonly string[],
): StatusKey => {
  if (entry.status === "uncertain")
    return pending.includes(entry.fieldKey) ? "uncertain" : "edited";
  if (Object.hasOwn(review.values, entry.fieldKey)) return "edited";
  return entry.status;
};

const STATUS_CHIP: Record<StatusKey, string> = {
  filled: "text-v3-ok-text bg-v3-ok-tint shadow-none",
  uncertain: "",
  blank: "text-v3-text-3",
  identifier: "text-v3-brand-strong bg-v3-brand-tint shadow-none",
  signature: "text-v3-text-3",
  edited: "text-v3-text-2",
};

// One field's editor, by type.
const ValueInput = ({
  field,
  value,
  id,
  onChange,
  placeholder,
}: {
  field: FormField;
  value: string | null;
  id: string;
  onChange: (value: string | null) => void;
  placeholder?: string;
}): JSX.Element => {
  const { t } = useTranslation();
  if (field.type === "checkbox")
    return (
      <Checkbox
        checked={value === "true"}
        id={id}
        label={
          value === "true"
            ? t("forms.review.checked")
            : t("forms.review.unchecked")
        }
        onCheckedChange={(checked) => onChange(checked ? "true" : null)}
      />
    );
  if (field.type === "radio" || field.type === "select")
    return (
      <div className="flex items-center gap-1">
        <Select
          value={value ?? ""}
          onValueChange={(next) => onChange(next || null)}
        >
          <SelectTrigger
            aria-labelledby={`${id}-label`}
            className="bg-v3-panel"
            id={id}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {value && (
          <IconButton
            label={t("forms.review.card.clear")}
            onClick={() => onChange(null)}
          >
            <X />
          </IconButton>
        )}
      </div>
    );
  if (field.type === "multiline")
    return (
      <Textarea
        className="bg-v3-panel"
        id={id}
        placeholder={placeholder}
        rows={3}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value || null)}
      />
    );
  return (
    <Input
      className="bg-v3-panel"
      id={id}
      placeholder={placeholder}
      type={field.type === "date" ? "date" : "text"}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value || null)}
    />
  );
};

const EntryRow = ({
  field,
  entry,
  review,
  status,
  active,
  onReview,
  onReviewChange,
}: {
  field: FormField;
  entry: FormEntry;
  review: FormReviewState;
  status: StatusKey;
  active: boolean;
  onReview: () => void;
  onReviewChange: (review: FormReviewState) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const id = useId();
  const row = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (active)
      row.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [active]);
  const value = shownValue(entry, review);
  return (
    <li
      ref={row}
      className={cn(
        "space-y-2 rounded-[14px] bg-v3-surface-2 p-3.5 shadow-v3-hairline",
        status === "uncertain" &&
          "bg-v3-warn-tint shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--v3-warn)_35%,transparent)]",
        active && "ring-2 ring-v3-warn",
      )}
      data-status={status}
      data-testid="form-entry"
    >
      <div className="flex items-start justify-between gap-2">
        <label
          className="min-w-0 text-[14px] font-semibold leading-5 text-v3-text"
          htmlFor={id}
          id={`${id}-label`}
        >
          {field.label}
        </label>
        {status === "uncertain" ? (
          <button
            className="shrink-0"
            data-testid="form-entry-check"
            type="button"
            onClick={onReview}
          >
            <ChipLabel variant="warn">
              {t("forms.review.status.uncertain")}
            </ChipLabel>
          </button>
        ) : (
          <ChipLabel className={cn("shrink-0", STATUS_CHIP[status])}>
            {status === "identifier" && <Lock aria-hidden />}
            {status === "signature" && <PenLine aria-hidden />}
            {t(`forms.review.status.${status}`)}
          </ChipLabel>
        )}
      </div>
      {field.role === "signature" ? (
        <p className="text-[13.5px] leading-5 text-v3-text-3">
          {t("forms.review.signatureHint")}
        </p>
      ) : field.role === "identifier" ? (
        <>
          <ValueInput
            field={field}
            id={id}
            value={review.identifiers[field.key] || null}
            onChange={(next) =>
              onReviewChange(setIdentifier(review, field.key, next ?? ""))
            }
          />
          <p className="flex items-center gap-1.5 text-[12.5px] leading-[17px] text-v3-text-3">
            <Lock aria-hidden className="size-3.5" />
            {t("forms.review.identifierHint")}
          </p>
        </>
      ) : (
        <>
          <ValueInput
            field={field}
            id={id}
            placeholder={t("forms.review.blankPlaceholder")}
            value={value}
            onChange={(next) =>
              onReviewChange(setValue(review, field.key, next))
            }
          />
          {entry.sourceQuote &&
            !Object.hasOwn(review.values, entry.fieldKey) && (
              <p
                className="border-l-2 border-v3-border-strong pl-2.5 text-[12.5px] leading-[18px] text-v3-text-3"
                data-testid="form-entry-source"
              >
                {t(`forms.review.sources.${entry.sourceRef ?? "note"}`)}{" "}
                <q className="text-v3-text-2">{entry.sourceQuote}</q>
              </p>
            )}
        </>
      )}
    </li>
  );
};

/**
 * The review step: every field with its value. Uncertain values are checked
 * one at a time in the v3.2 review card (Accept, Edit, Clear) before the PDF
 * can be made; identifiers are typed here and never sent to the model.
 */
export const FormReview = ({
  form,
  review,
  pending,
  activeKey,
  phone,
  onReviewChange,
  onActiveKeyChange,
}: {
  form: CompletedForm;
  review: FormReviewState;
  pending: string[];
  activeKey: string | null;
  phone: boolean;
  onReviewChange: (review: FormReviewState) => void;
  onActiveKeyChange: (key: string | null) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const fields = new Map(form.fields.map((field) => [field.key, field]));
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const index = activeKey ? pending.indexOf(activeKey) : -1;
  const activeEntry = form.entries.find(
    (entry) => entry.fieldKey === activeKey,
  );
  const activeField = activeKey ? fields.get(activeKey) : undefined;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset per value shown.
  useEffect(() => {
    setEditing(false);
    setDraft(activeEntry?.value ?? "");
  }, [activeKey]);
  const next = (after: FormReviewState) => {
    onReviewChange(after);
    const rest = pending.filter((key) => key !== activeKey);
    onActiveKeyChange(
      rest[Math.min(Math.max(index, 0), rest.length - 1)] ?? null,
    );
  };
  const card =
    activeEntry && activeField && index >= 0 ? (
      <ReviewCardShell
        actions={
          editing ? (
            <form
              className="space-y-3"
              onSubmit={(event) => {
                event.preventDefault();
                next(
                  setValue(review, activeEntry.fieldKey, draft.trim() || null),
                );
              }}
            >
              <div className="space-y-1.5">
                <label
                  className="block text-[13px] font-semibold text-v3-text-2"
                  htmlFor="form-review-edit"
                  id="form-review-edit-label"
                >
                  {t("visit.workspace.verify.card.editLabel")}
                </label>
                <ValueInput
                  field={activeField}
                  id="form-review-edit"
                  value={draft || null}
                  onChange={(value) => setDraft(value ?? "")}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <button className={REVIEW_PRIMARY} type="submit">
                  <Check aria-hidden className="size-4" />
                  {t("visit.workspace.verify.card.save")}
                </button>
                <button
                  className={REVIEW_QUIET}
                  type="button"
                  onClick={() => setEditing(false)}
                >
                  {t("visit.workspace.verify.card.cancel")}
                </button>
              </div>
            </form>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button
                className={REVIEW_PRIMARY}
                data-testid="form-review-accept"
                type="button"
                onClick={() => next(accept(review, activeEntry.fieldKey))}
              >
                <Check aria-hidden className="size-4" />
                {t("visit.workspace.verify.card.accept")}
              </button>
              <button
                className={REVIEW_QUIET}
                data-testid="form-review-edit"
                type="button"
                onClick={() => setEditing(true)}
              >
                <Pencil aria-hidden className="size-4" />
                {t("visit.workspace.verify.card.edit")}
              </button>
              <button
                className={REVIEW_QUIET}
                data-testid="form-review-clear"
                type="button"
                onClick={() =>
                  next(setValue(review, activeEntry.fieldKey, null))
                }
              >
                <X aria-hidden className="size-4" />
                {t("forms.review.card.clear")}
              </button>
            </div>
          )
        }
        before={`${activeField.label}: `}
        className={phone ? undefined : "sticky top-0 z-10 mx-5 mb-3"}
        closeLabel={t("visit.workspace.verify.card.close")}
        explanation={
          activeEntry.sourceQuote
            ? t("forms.review.card.explainUncertain")
            : t("forms.review.card.explainNoQuote")
        }
        index={index}
        layout={phone ? "sheet" : "card"}
        nextLabel={t("visit.workspace.verify.card.next")}
        positionLabel={t("visit.workspace.verify.card.position", {
          current: index + 1,
          total: pending.length,
        })}
        previousLabel={t("visit.workspace.verify.card.previous")}
        title={t("forms.review.card.title")}
        total={pending.length}
        value={
          activeField.type === "checkbox"
            ? t("forms.review.checked")
            : (activeEntry.value ?? "")
        }
        onClose={() => onActiveKeyChange(null)}
        onNext={() =>
          onActiveKeyChange(pending[(index + 1) % pending.length] ?? null)
        }
        onPrevious={() =>
          onActiveKeyChange(
            pending[(index - 1 + pending.length) % pending.length] ?? null,
          )
        }
      />
    ) : null;
  return (
    <div className="pb-6 pt-1" data-testid="form-review">
      <div className="px-5">
        <h2 className="font-v3-display text-[22px] font-bold leading-7 tracking-[-0.01em]">
          {form.name}
        </h2>
        <p className="mt-1.5 text-[15px] leading-[22px] text-v3-text-2">
          {t("forms.review.description")}
        </p>
      </div>
      <div className="mt-4">{card}</div>
      <ul className="space-y-2.5 px-5">
        {form.entries.map((entry) => {
          const field = fields.get(entry.fieldKey);
          if (!field) return null;
          return (
            <EntryRow
              key={entry.fieldKey}
              active={entry.fieldKey === activeKey}
              entry={entry}
              field={field}
              review={review}
              status={statusOf(entry, review, pending)}
              onReview={() => onActiveKeyChange(entry.fieldKey)}
              onReviewChange={onReviewChange}
            />
          );
        })}
      </ul>
    </div>
  );
};
